import WorkerRegistrationWizard from '../components/onboarding/WorkerRegistrationWizard';
import { registerInvitedWorker } from "../inviteApply";
import { isFacilityInvitationRole, registerFacilityAccount } from "../services/access/facilityInvitations";
import { useState } from "react";
import {
  createUserWithEmailAndPassword,
  deleteUser,
  signInWithEmailAndPassword,
} from "firebase/auth";

import { auth, db } from "../firebase";

import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";


export default function Login({ initialMode = "login", onRegistrationState = () => {} }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [firstName, setFirstName] = useState("");
const [lastName, setLastName] = useState("");
const [phone, setPhone] = useState("");
const [homeZip, setHomeZip] = useState("");
const [city, setCity] = useState("");
const [stateCode, setStateCode] = useState("TX");
const [primaryRole, setPrimaryRole] = useState("LVN");
const [yearsExperience, setYearsExperience] = useState("");
const [maxTravelMiles, setMaxTravelMiles] = useState(25);
const [hasTransportation, setHasTransportation] = useState(true);

  async function handleLogin(e) {
    e.preventDefault();
    setMsg("");
    setBusy(true);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      setMsg(err?.message || "Login failed.");
    } finally {
      setBusy(false);
    }
  }

async function handleInvitationAcceptance(e) {
  e.preventDefault();
  setMsg("");

  const emailLower = email.trim().toLowerCase();

  if (!emailLower || !password) {
    setMsg("Please enter the invited email and create a password.");
    return;
  }

  setBusy(true);

  let createdUser = null;
  let accountCreated = false;

  try {
    let cred;
    try {
      cred = await createUserWithEmailAndPassword(auth, emailLower, password);
      createdUser = cred.user;
    } catch (error) {
      if (error.code !== "auth/email-already-in-use") throw error;
      // Linking requires proving ownership, never replacing the existing UID.
      cred = await signInWithEmailAndPassword(auth, emailLower, password);
    }

    console.log("INVITE ACCEPT STEP 1 — AUTH CREATED", {
      uid: cred.user.uid,
      email: cred.user.email,
    });

    const inviteRef = doc(db, "invites", emailLower);

    console.log(
      "INVITE ACCEPT STEP 2 — READING INVITATION",
      emailLower
    );

    const inviteSnap = await getDoc(inviteRef);

    console.log("INVITE ACCEPT STEP 3 — INVITATION READ", {
      exists: inviteSnap.exists(),
      inviteId: inviteSnap.id,
    });

    if (!inviteSnap.exists()) {
      throw new Error(
        "No invitation was found for this email address."
      );
    }

    const invite = inviteSnap.data() || {};

    if (isFacilityInvitationRole(invite.platformRole || invite.role)) {
      await registerFacilityAccount(cred.user, inviteSnap.id);
      accountCreated = true;
      setMsg("Invitation accepted. Your facility account is now active.");
      return;
    }

    const accountType = String(
      invite.accountType || ""
    )
      .trim()
      .toLowerCase();

    const invitationSource = String(
      invite.source || ""
    )
      .trim()
      .toLowerCase();

    // The auth listener can finish the same registration while this screen is
    // loading. The Worker transaction handles that retry without resetting it.
    if (accountType === "worker" &&
        String(invite.platformRole || invite.role || "").trim().toLowerCase() === "worker") {
      await registerInvitedWorker(cred.user, inviteSnap.id);
      accountCreated = true;
      setMsg("Registration received. Your account is awaiting admin approval.");
      return;
    }

    const invitationStatus = String(
      invite.inviteStatus ||
        invite.invitationStatus ||
        invite.status ||
        ""
    )
      .trim()
      .toLowerCase();

    if (!["sent", "invited", "pending"].includes(invitationStatus)) {
      throw new Error(
        "This invitation has already been accepted or is no longer active."
      );
    }

    const tenantId = String(invite.tenantId || "")
      .trim()
      .toLowerCase();

    if (!tenantId) {
      throw new Error("The invitation is missing its tenant.");
    }

    const rawPlatformRole = String(
      invite.platformRole ||
        invite.role ||
        invite.primaryRole ||
        ""
    )
      .trim()
      .toLowerCase();

    const roleAliases = {
      planner: "facility_planner",
      facilityplanner: "facility_planner",
      owneradmin: "owner_admin",
      operationsadmin: "operations_admin",
      financeadmin: "finance_admin",
    };

    const platformRole =
      roleAliases[rawPlatformRole] || rawPlatformRole;

    const allowedInternalAdminRoles = [
      "owner_admin",
      "operations_admin",
      "hr_admin",
      "credentialing_admin",
      "finance_admin",
    ];

    const allowedFacilityRoles = [
      "facility_planner",
      "facility_admin_viewer",
    ];

    const facilityAccountTypes = [
      "facility",
      "facility_account",
      "facility_user",
      "facility_planner",
      "facility_admin_viewer",
    ];

    const isInternalAdminInvitation =
      accountType === "internal_admin" &&
      allowedInternalAdminRoles.includes(platformRole);

    const isFacilityInvitation =
      allowedFacilityRoles.includes(platformRole) &&
      (
        facilityAccountTypes.includes(accountType) ||
        invitationSource === "facility_account_management"
      );

    if (!isInternalAdminInvitation && !isFacilityInvitation) {
      throw new Error(
        "This invitation contains an unsupported account type or role."
      );
    }

    const invitationFacilityIds = [
      invite.facilityId,

      ...(Array.isArray(invite.facilityIds)
        ? invite.facilityIds
        : []),

      ...(Array.isArray(invite.allowedFacilityIds)
        ? invite.allowedFacilityIds
        : []),

      ...(Array.isArray(invite.assignedFacilityIds)
        ? invite.assignedFacilityIds
        : []),
    ]
      .map((facilityId) =>
        String(facilityId || "")
          .trim()
          .toLowerCase()
      )
      .filter(
        (facilityId, index, array) =>
          facilityId &&
          facilityId !== "all" &&
          array.indexOf(facilityId) === index
      );

    if (
      isFacilityInvitation &&
      invitationFacilityIds.length === 0
    ) {
      throw new Error(
        "The facility invitation is missing its assigned facility."
      );
    }

    const accessScope = isFacilityInvitation
      ? "selected_facilities"
      : invite.accessScope === "selected_facilities"
        ? "selected_facilities"
        : "all_facilities";

    const allowedFacilityIds =
      accessScope === "selected_facilities"
        ? invitationFacilityIds
        : [];

    const primaryFacilityId =
      allowedFacilityIds.length > 0
        ? allowedFacilityIds[0]
        : null;

    const name = String(
      invite.name ||
        invite.displayName ||
        emailLower
    ).trim();

    const resolvedAccountType = isInternalAdminInvitation
      ? "internal_admin"
      : accountType || "facility_account";

    console.log("INVITE ACCEPT RESOLVED PROFILE", {
      accountType: resolvedAccountType,
      platformRole,
      tenantId,
      accessScope,
      allowedFacilityIds,
      primaryFacilityId,
    });

    const userRef = doc(db, "users", cred.user.uid);
    const batch = writeBatch(db);

    batch.set(
      userRef,
      {
        uid: cred.user.uid,
        email: emailLower,
        emailLower,

        name,
        displayName: name,
        phone: String(invite.phone || "").trim(),

        tenantId,
        accountType: resolvedAccountType,

        role: platformRole,
        platformRole,
        primaryRole: platformRole.toUpperCase(),
        position: platformRole.toUpperCase(),

        accessScope,
        allowedFacilityIds,
        facilityIds: allowedFacilityIds,
        assignedFacilityIds: allowedFacilityIds,
        facilityId: primaryFacilityId,
        primaryFacilityId,

        active: true,
        status: "active",
        archived: false,
        disabled: false,

        registrationStatus: "registered",
        registrationApproved: true,

        canAccessDashboard: true,
        canAccessOnboarding: false,

        canViewFacilityPulse:
          platformRole === "facility_admin_viewer" ||
          invite.canViewFacilityPulse === true,

        onboardingStatus: "not_applicable",
        onboardingCompleted: true,

        complianceStatus: "not_applicable",
        workerStatus: "not_applicable",
        workerOperationalState: "NOT_APPLICABLE",

        profileCompleted: true,

        invitationId: inviteSnap.id,
        invitationAcceptedAt: serverTimestamp(),

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    batch.update(inviteRef, {
      status: "accepted",
      inviteStatus: "accepted",
      invitationStatus: "accepted",
      registrationStatus: "registered",

      acceptedBy: cred.user.uid,
      acceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    console.log(
      "INVITE ACCEPT STEP 4 — COMMITTING USER AND INVITATION"
    );

    await batch.commit();

    console.log(
      "INVITE ACCEPT STEP 5 — TRANSACTION COMPLETED"
    );

    accountCreated = true;

    setMsg(
      isFacilityInvitation
        ? "Invitation accepted. Your facility account is now active."
        : "Invitation accepted. Your Internal Admin account is now active."
    );
  } catch (err) {
    console.error("Invitation acceptance failed:", err);

    if (createdUser && !accountCreated) {
      try {
        // Do not orphan a profile committed by the auth listener or a write
        // whose acknowledgement was lost. If this read fails, keep identity.
        const profile = await getDoc(doc(db, "users", createdUser.uid));
        if (!profile.exists()) await deleteUser(createdUser);
      } catch (deleteErr) {
        console.error(
          "Unable to remove incomplete authentication account:",
          deleteErr
        );
      }
    }

    setMsg(err?.message || "Unable to accept invitation.");
  } finally {
    setBusy(false);
  }
}

async function handleSignup(e) {
  e.preventDefault();
  setMsg("");

  if (!email.trim() || !password) {
    setMsg("Please enter email and password.");
    return;
  }

  if (
    !firstName.trim() ||
    !lastName.trim() ||
    !phone.trim() ||
    !homeZip.trim() ||
    !city.trim() ||
    !stateCode.trim() ||
    !primaryRole
  ) {
    setMsg("Please complete all registration fields.");
    return;
  }

  setBusy(true);
  onRegistrationState('submitting');

  try {
    const emailLower = email.trim().toLowerCase();

    const cred = await createUserWithEmailAndPassword(
      auth,
      emailLower,
      password
    );

    await setDoc(
      doc(db, "users", cred.user.uid),
      {
        uid: cred.user.uid,
        email: emailLower,
        emailLower,

        firstName: firstName.trim(),
        lastName: lastName.trim(),
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        phone: phone.trim(),

        homeZip: homeZip.trim(),
        zip: homeZip.trim(),
        city: city.trim(),
        state: stateCode.trim().toUpperCase(),

        primaryRole,
        position: primaryRole,
        workerType: primaryRole,
        secondaryRoles: [],

        yearsExperience: Number(yearsExperience || 0),
        travelRadiusMiles: Number(maxTravelMiles || 25),
        hasTransportation,

        role: "worker",

        registrationStatus: "pending",
        registrationApproved: false,

        canAccessOnboarding: false,
        canAccessDashboard: false,

        onboardingStatus: "locked",
        onboardingCompleted: false,

        complianceStatus: "not_started",
        workerStatus: "registration_pending",
        workerOperationalState: "REGISTRATION_PENDING",

        status: "pending_registration_approval",

        archived: false,
        disabled: false,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    setMsg("Signup created. Your registration is pending admin approval.");
    onRegistrationState('submitted');
  } catch (err) {
    setMsg(err?.message || "Signup failed.");
    onRegistrationState('failed');
  } finally {
    setBusy(false);
  }
}

  return (
  <div className={mode === 'signup' ? 'worker-signup-shell' : undefined} style={{ maxWidth: 520 }}>
    <h2>ShiftBridge</h2>

    <div
      style={{
        display: "flex",
        gap: 10,
        marginBottom: 12,
        flexWrap: "wrap",
      }}
    >
      <button
        type="button"
        onClick={() => {
          setMode("login");
          setMsg("");
        }}
        disabled={mode === "login"}
      >
        Login
      </button>

      <button
        type="button"
        onClick={() => {
          setMode("signup");
          setMsg("");
        }}
        disabled={mode === "signup"}
      >
        Worker Signup
      </button>

         <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          console.log("ACCEPT INVITATION TAB CLICKED");
          setMode("invite");
          setMsg("");
        }}
        disabled={mode === "invite"}
      >
        Accept Invitation
      </button>
    </div>

    <div
  style={{
    marginBottom: 12,
    color: "#475569",
    fontSize: 12,
    fontWeight: 800,
  }}
>
  Login screen mode: {mode}
</div>

    {mode === 'signup' ? <WorkerRegistrationWizard busy={busy} error={msg} onSubmit={handleSignup} steps={[{title:'About you',content:<><label>First name<input
              required
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={busy}
            /></label><label>Last name<input
              required
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={busy}
            /></label><label>Phone<input
              required
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={busy}
            /></label><label>ZIP code<input
              required
              placeholder="ZIP Code"
              value={homeZip}
              onChange={(e) => setHomeZip(e.target.value)}
              disabled={busy}
            /></label><label>City<input
              required
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={busy}
            /></label><label>State<input
              required
              placeholder="State"
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              disabled={busy}
            /></label></>},{title:'Work preferences',content:<><label>Role<select
              required
              value={primaryRole}
              onChange={(e) => setPrimaryRole(e.target.value)}
              disabled={busy}
            >
              <option value="CNA">CNA</option>
              <option value="LVN">LVN</option>
              <option value="RN">RN</option>
              <option value="MED_AIDE">Med Aide</option>
              <option value="CAREGIVER">Caregiver</option>
            </select></label><label>Experience<select
  required
  value={yearsExperience}
  onChange={(e) => setYearsExperience(e.target.value)}
  disabled={busy}
>
  <option value="">Select Experience</option>
  <option value="0">New / No Experience</option>
  <option value="0.25">0–3 Months</option>
  <option value="0.5">3–6 Months</option>
  <option value="1">6–12 Months</option>
  <option value="2">1–2 Years</option>
  <option value="3">2–3 Years</option>
  <option value="5">3–5 Years</option>
  <option value="10">5–10 Years</option>
  <option value="11">10+ Years</option>
</select></label><label>Maximum travel distance (miles)<input
  required
  type="number"
  min="1"
  placeholder="Miles willing to travel"
  value={maxTravelMiles}
  onChange={(e) => setMaxTravelMiles(e.target.value)}
  disabled={busy}
/></label><label style={{ fontWeight: 800 }}>
              <input
                type="checkbox"
                checked={hasTransportation}
                onChange={(e) => setHasTransportation(e.target.checked)}
                disabled={busy}
              />{" "}
              Reliable Transportation
            </label></>},{title:'Account',content:<><label>Email<input
          required
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        /></label><label>Password<input
          required
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
        /></label></>}]}/> : (<form
  onSubmit={
    mode === "login"
      ? handleLogin
      : mode === "invite"
      ? handleInvitationAcceptance
      : handleSignup
  }
>
      <div style={{ display: "grid", gap: 10 }}>

{mode === "invite" ? (
  <div
    style={{
      padding: 12,
      borderRadius: 10,
      background: "#eff6ff",
      color: "#1e3a8a",
      fontWeight: 700,
    }}
  >
    Enter the exact email address that received the invitation and
    create your account password.
  </div>
) : null}

        {mode === "signup" ? (
          <>
            <h3 style={{ margin: "6px 0 0" }}>Registration Intake</h3>

            <input
              required
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={busy}
            />

            <input
              required
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={busy}
            />

            <input
              required
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={busy}
            />

            <input
              required
              placeholder="ZIP Code"
              value={homeZip}
              onChange={(e) => setHomeZip(e.target.value)}
              disabled={busy}
            />

            <input
              required
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={busy}
            />

            <input
              required
              placeholder="State"
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              disabled={busy}
            />

            <select
              required
              value={primaryRole}
              onChange={(e) => setPrimaryRole(e.target.value)}
              disabled={busy}
            >
              <option value="CNA">CNA</option>
              <option value="LVN">LVN</option>
              <option value="RN">RN</option>
              <option value="MED_AIDE">Med Aide</option>
              <option value="CAREGIVER">Caregiver</option>
            </select>

           <select
  required
  value={yearsExperience}
  onChange={(e) => setYearsExperience(e.target.value)}
  disabled={busy}
>
  <option value="">Select Experience</option>
  <option value="0">New / No Experience</option>
  <option value="0.25">0–3 Months</option>
  <option value="0.5">3–6 Months</option>
  <option value="1">6–12 Months</option>
  <option value="2">1–2 Years</option>
  <option value="3">2–3 Years</option>
  <option value="5">3–5 Years</option>
  <option value="10">5–10 Years</option>
  <option value="11">10+ Years</option>
</select>

<div style={{ fontWeight: 800 }}>Maximum Travel Distance</div>

<input
  required
  type="number"
  min="1"
  placeholder="Miles willing to travel"
  value={maxTravelMiles}
  onChange={(e) => setMaxTravelMiles(e.target.value)}
  disabled={busy}
/>

            <label style={{ fontWeight: 800 }}>
              <input
                type="checkbox"
                checked={hasTransportation}
                onChange={(e) => setHasTransportation(e.target.checked)}
                disabled={busy}
              />{" "}
              Reliable Transportation
            </label>

            <h3 style={{ margin: "10px 0 0" }}>Account Login</h3>
          </>
        ) : null}

        <input
          required
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />

        <input
          required
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
        />

            <button type="submit" disabled={busy}>
          {busy
            ? "Please wait..."
            : mode === "login"
            ? "Login"
            : mode === "invite"
            ? "Accept Invitation"
            : "Submit Worker Registration"}
        </button>
      </div>
    </form>)}

    {msg ? (
      <pre
        style={{
          marginTop: 16,
          whiteSpace: "pre-wrap",
          background: "#f6f6f6",
          padding: 12,
          borderRadius: 8,
        }}
      >
        {msg}
      </pre>
    ) : null}
  </div>
);
}
